/**
 * Create Schedule Modal
 *
 * Modal form for creating a new agent schedule.
 * Supports cron, event triggers, and dependency configuration.
 * Features a friendly schedule picker with preset frequencies,
 * cron validation, human-readable preview, and next run times.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Clock,
  Zap,
  GitBranch,
  Plus,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import {
  AgentScheduleService,
  type CreateScheduleData,
  type Schedule,
  type EventType,
} from '../services/agentScheduleService';

// ---------------------------------------------------------------------------
// Cron validation & human-readable helpers
// ---------------------------------------------------------------------------

interface CronValidation {
  valid: boolean;
  error?: string;
  human?: string;
}

const FIELD_RANGES: [string, number, number][] = [
  ['minute', 0, 59],
  ['hour', 0, 23],
  ['day-of-month', 1, 31],
  ['month', 1, 12],
  ['day-of-week', 0, 7], // 0 and 7 both = Sunday
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function validateCronField(value: string, fieldName: string, min: number, max: number): string | null {
  // Wildcard
  if (value === '*') return null;

  // Step: */N or N-M/S
  if (value.includes('/')) {
    const [range, stepStr] = value.split('/');
    const step = parseInt(stepStr, 10);
    if (isNaN(step) || step < 1) return `${fieldName}: invalid step value "${stepStr}"`;
    if (range !== '*') {
      const rangeErr = validateCronField(range, fieldName, min, max);
      if (rangeErr) return rangeErr;
    }
    return null;
  }

  // List: 1,3,5
  if (value.includes(',')) {
    for (const item of value.split(',')) {
      const err = validateCronField(item.trim(), fieldName, min, max);
      if (err) return err;
    }
    return null;
  }

  // Range: 1-5
  if (value.includes('-')) {
    const [startStr, endStr] = value.split('-');
    const start = parseInt(startStr, 10);
    const end = parseInt(endStr, 10);
    if (isNaN(start) || isNaN(end)) return `${fieldName}: invalid range "${value}"`;
    if (start < min || start > max) return `${fieldName}: ${start} out of range (${min}-${max})`;
    if (end < min || end > max) return `${fieldName}: ${end} out of range (${min}-${max})`;
    if (start > end) return `${fieldName}: range start ${start} > end ${end}`;
    return null;
  }

  // Single number
  const num = parseInt(value, 10);
  if (isNaN(num)) return `${fieldName}: "${value}" is not a valid number`;
  if (num < min || num > max) return `${fieldName}: ${num} out of range (${min}-${max})`;
  return null;
}

function validateCron(expr: string): CronValidation {
  const trimmed = expr.trim();
  if (!trimmed) return { valid: false, error: 'Cron expression is required' };

  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) {
    return { valid: false, error: `Cron must have exactly 5 fields (got ${parts.length}). Format: minute hour day-of-month month day-of-week` };
  }

  for (let i = 0; i < 5; i++) {
    const [fieldName, min, max] = FIELD_RANGES[i];
    const err = validateCronField(parts[i], fieldName, min, max);
    if (err) return { valid: false, error: err };
  }

  return { valid: true, human: cronToHuman(trimmed) };
}

function cronToHuman(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;

  const [minute, hour, dom, month, dow] = parts;

  // Every minute
  if (minute === '*' && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return 'Runs every minute';
  }

  // Every N minutes
  if (minute.startsWith('*/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    const n = parseInt(minute.split('/')[1], 10);
    return `Runs every ${n} minute${n === 1 ? '' : 's'}`;
  }

  // Every hour at :MM
  if (!minute.includes('*') && !minute.includes('/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return `Runs every hour at :${minute.padStart(2, '0')}`;
  }

  // Every N hours
  if (minute === '0' && hour.startsWith('*/') && dom === '*' && month === '*' && dow === '*') {
    const n = parseInt(hour.split('/')[1], 10);
    return `Runs every ${n} hour${n === 1 ? '' : 's'}`;
  }

  // Daily at HH:MM
  if (!minute.includes('*') && !minute.includes('/') && !hour.includes('*') && !hour.includes('/') && dom === '*' && month === '*' && dow === '*') {
    return `Runs daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} UTC`;
  }

  // Weekly on DAY at HH:MM
  if (!minute.includes('*') && !minute.includes('/') && !hour.includes('*') && !hour.includes('/') && dom === '*' && month === '*' && dow !== '*') {
    const dayNum = parseInt(dow, 10);
    const dayName = DAY_NAMES[dayNum % 7] || dow;
    return `Runs weekly on ${dayName} at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} UTC`;
  }

  // Monthly on Nth at HH:MM
  if (!minute.includes('*') && !minute.includes('/') && !hour.includes('*') && !hour.includes('/') && !dom.includes('*') && month === '*' && dow === '*') {
    return `Runs monthly on day ${dom} at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} UTC`;
  }

  // Fallback
  return `Cron: ${expr}`;
}

/**
 * Compute the next N scheduled run times for a cron expression.
 * Simple implementation that handles common patterns.
 */
function getNextRunTimes(expr: string, count: number = 3): Date[] {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return [];

  const [minuteField, hourField, domField, , dowField] = parts;
  const results: Date[] = [];
  const now = new Date();
  // Start from the next minute boundary
  const cursor = new Date(now);
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);

  // Expand a cron field into matching numbers
  const expand = (field: string, min: number, max: number): number[] | null => {
    if (field === '*') return null; // wildcard = all
    if (field.startsWith('*/')) {
      const step = parseInt(field.split('/')[1], 10);
      if (isNaN(step) || step < 1) return null;
      const vals: number[] = [];
      for (let i = min; i <= max; i += step) vals.push(i);
      return vals;
    }
    if (field.includes(',')) {
      return field.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    }
    if (field.includes('-')) {
      const [a, b] = field.split('-').map(s => parseInt(s, 10));
      const vals: number[] = [];
      for (let i = a; i <= b; i++) vals.push(i);
      return vals;
    }
    const n = parseInt(field, 10);
    return isNaN(n) ? null : [n];
  };

  const minuteVals = expand(minuteField, 0, 59);
  const hourVals = expand(hourField, 0, 23);
  const domVals = expand(domField, 1, 31);
  const dowVals = expand(dowField, 0, 6)?.map(d => d % 7) ?? null; // normalize 7 -> 0

  const matchesMinute = (m: number) => !minuteVals || minuteVals.includes(m);
  const matchesHour = (h: number) => !hourVals || hourVals.includes(h);
  const matchesDom = (d: number) => !domVals || domVals.includes(d);
  const matchesDow = (d: number) => !dowVals || dowVals.includes(d);

  // Scan up to 366 * 24 * 60 minutes ahead (1 year)
  const maxIter = 366 * 24 * 60;
  for (let i = 0; i < maxIter && results.length < count; i++) {
    if (
      matchesMinute(cursor.getUTCMinutes()) &&
      matchesHour(cursor.getUTCHours()) &&
      matchesDom(cursor.getUTCDate()) &&
      matchesDow(cursor.getUTCDay())
    ) {
      results.push(new Date(cursor));
    }
    cursor.setMinutes(cursor.getMinutes() + 1);
  }

  return results;
}

// ---------------------------------------------------------------------------
// Duration formatting helper
// ---------------------------------------------------------------------------

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

// ---------------------------------------------------------------------------
// Preset types
// ---------------------------------------------------------------------------

type SchedulePreset = 'hourly' | 'every6h' | 'daily' | 'weekly' | 'custom';

interface PresetOption {
  id: SchedulePreset;
  label: string;
  description: string;
}

const PRESETS: PresetOption[] = [
  { id: 'hourly', label: 'Every hour', description: 'At minute 0 of every hour' },
  { id: 'every6h', label: 'Every 6 hours', description: 'At 00:00, 06:00, 12:00, 18:00 UTC' },
  { id: 'daily', label: 'Daily', description: 'Once per day at a chosen time' },
  { id: 'weekly', label: 'Weekly', description: 'Once per week on a chosen day and time' },
  { id: 'custom', label: 'Custom cron expression', description: 'Full control with a raw cron expression' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
const TIME_OPTIONS = HOUR_OPTIONS.flatMap((hour) => (
  MINUTE_OPTIONS.map((minute) => ({
    value: `${hour}:${minute}`,
    label: `${hour}:${minute} UTC`,
  }))
));

const MODAL_CONTROL_BASE_CLASS = 'rounded-xl border border-gray-200/90 dark:border-white/10 bg-gray-50/90 dark:bg-white/[0.04] text-gray-900 dark:text-gray-100 shadow-sm shadow-black/[0.02] dark:shadow-none placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#FFB52B]/50 dark:focus:border-[#FFB52B]/30 focus:ring-2 focus:ring-[#FFB52B]/10';
const MODAL_SELECT_ARROW_CLASS = "appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m2%204%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat dark:[color-scheme:dark]";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CreateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (schedule: Schedule) => void;
  token: string;
  existingSchedules: Schedule[];
}

const DEFAULT_PROMPT = `# Scheduled Agent
#
# IMPORTANT: You are an autonomous scheduled agent. Key behaviours:
# 1. Read recent context with: mlst-rescript agent-schedules logs SCHEDULE_ID --limit 50
# 2. Persist durable state with: mlst-rescript agent-schedules log-write SCHEDULE_ID --level context --message "..."
# 3. Be idempotent -- check whether the work was already done before doing it again
# 4. Do not ask the user what to do next during the run
# 5. If you find a directly fixable in-scope issue, remediate it in this run; otherwise log the blocker and finish cleanly
# 6. Do not rely on workspace-local memory files as the system of record
# 7. Log notable progress/errors clearly so they appear in the run history
# 8. Write a context log as soon as you choose the bounded target for this run
# 9. Keep runs focused and bounded
#
# Your task:
`;

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  token,
  existingSchedules,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerMode, setTriggerMode] = useState<'cron' | 'event' | 'dependency'>('cron');

  // Schedule picker state
  const [selectedPreset, setSelectedPreset] = useState<SchedulePreset>('daily');
  const [dailyHour, setDailyHour] = useState('03');
  const [dailyMinute, setDailyMinute] = useState('00');
  const [weeklyDay, setWeeklyDay] = useState('1'); // Monday
  const [weeklyHour, setWeeklyHour] = useState('03');
  const [weeklyMinute, setWeeklyMinute] = useState('00');
  const [customCron, setCustomCron] = useState('0 3 * * *');

  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [eventDelaySeconds, setEventDelaySeconds] = useState(60);
  const [dependsOnScheduleId, setDependsOnScheduleId] = useState('');
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [model, setModel] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState('high');
  const [maxTurns, setMaxTurns] = useState(50);
  const [maxRuntimeSeconds, setMaxRuntimeSeconds] = useState(600);
  const [maxCostUsd, setMaxCostUsd] = useState('');

  const [availableEvents, setAvailableEvents] = useState<EventType[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dailyTime = `${dailyHour}:${dailyMinute}`;
  const weeklyTime = `${weeklyHour}:${weeklyMinute}`;

  // Derive the effective cron expression from the preset + picker state
  const effectiveCron = useMemo(() => {
    switch (selectedPreset) {
      case 'hourly':
        return '0 * * * *';
      case 'every6h':
        return '0 */6 * * *';
      case 'daily':
        return `${parseInt(dailyMinute, 10)} ${parseInt(dailyHour, 10)} * * *`;
      case 'weekly':
        return `${parseInt(weeklyMinute, 10)} ${parseInt(weeklyHour, 10)} * * ${weeklyDay}`;
      case 'custom':
        return customCron;
    }
  }, [selectedPreset, dailyHour, dailyMinute, weeklyDay, weeklyHour, weeklyMinute, customCron]);

  // Validate the effective cron
  const cronValidation = useMemo(() => validateCron(effectiveCron), [effectiveCron]);

  // Compute next 3 run times
  const nextRuns = useMemo(() => {
    if (!cronValidation.valid) return [];
    return getNextRunTimes(effectiveCron, 3);
  }, [effectiveCron, cronValidation.valid]);

  // Load available events
  useEffect(() => {
    if (!isOpen) return;
    AgentScheduleService.getEventTypes(token)
      .then(setAvailableEvents)
      .catch(() => {/* ignore - events endpoint may not exist yet */});
  }, [isOpen, token]);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setTriggerMode('cron');
      setSelectedPreset('daily');
      setDailyHour('03');
      setDailyMinute('00');
      setWeeklyDay('1');
      setWeeklyHour('03');
      setWeeklyMinute('00');
      setCustomCron('0 3 * * *');
      setSelectedEvents([]);
      setEventDelaySeconds(60);
      setDependsOnScheduleId('');
      setPrompt(DEFAULT_PROMPT);
      setModel('');
      setReasoningEffort('high');
      setMaxTurns(50);
      setMaxRuntimeSeconds(600);
      setMaxCostUsd('');
      setError(null);
    }
  }, [isOpen]);

  const toggleEvent = (evt: string) => {
    setSelectedEvents((prev) =>
      prev.includes(evt)
        ? prev.filter((e) => e !== evt)
        : [...prev, evt]
    );
  };

  const updateDailyTime = (value: string) => {
    const [hour = '03', minute = '00'] = value.split(':');
    setDailyHour(hour);
    setDailyMinute(minute);
  };

  const updateWeeklyTime = (value: string) => {
    const [hour = '03', minute = '00'] = value.split(':');
    setWeeklyHour(hour);
    setWeeklyMinute(minute);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!prompt.trim()) {
      setError('Prompt is required');
      return;
    }
    if (triggerMode === 'cron') {
      if (!cronValidation.valid) {
        setError(cronValidation.error || 'Invalid cron expression');
        return;
      }
    }
    if (triggerMode === 'event' && selectedEvents.length === 0) {
      setError('At least one event trigger is required');
      return;
    }
    if (triggerMode === 'dependency' && !dependsOnScheduleId) {
      setError('Select a schedule to depend on');
      return;
    }

    setCreating(true);
    setError(null);

    const data: CreateScheduleData = {
      name: name.trim(),
      description: description.trim() || undefined,
      prompt: prompt.trim(),
      cronExpression: triggerMode === 'cron' ? effectiveCron : null,
      eventTriggers: triggerMode === 'event' ? selectedEvents : undefined,
      eventDelaySeconds: triggerMode === 'event' ? eventDelaySeconds : undefined,
      dependsOnScheduleId: triggerMode === 'dependency' ? dependsOnScheduleId : null,
      model: model || undefined,
      reasoningEffort,
      maxTurns,
      maxRuntimeSeconds,
      maxCostUsd: maxCostUsd ? parseFloat(maxCostUsd) : null,
    };

    try {
      const schedule = await AgentScheduleService.create(data, token);
      onCreated(schedule);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const formatRunTime = (d: Date) => {
    const dayName = DAY_NAMES[d.getUTCDay()];
    const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const date = d.getUTCDate();
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${dayName}, ${month} ${date} at ${hh}:${mm} UTC`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full sm:max-w-xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-gray-200/90 dark:border-white/12 bg-white/95 dark:bg-[#1a2332]/95 backdrop-blur-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-5 border-b border-gray-200/90 dark:border-white/10 bg-white/95 dark:bg-[#1a2332]/95 backdrop-blur-2xl">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">New Agent Schedule</h2>
          <button type="button" onClick={onClose} aria-label="Close modal" className="p-2.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center">
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
              <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="schedule-name" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Name *</label>
            <input
              id="schedule-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Metadata Generation"
              maxLength={200}
              className={`w-full px-3 py-2 text-sm ${MODAL_CONTROL_BASE_CLASS}`}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="schedule-description" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description</label>
            <input
              id="schedule-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this scheduled agent do?"
              className={`w-full px-3 py-2 text-sm ${MODAL_CONTROL_BASE_CLASS}`}
            />
          </div>

          {/* Trigger type selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Trigger Type</label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'cron' as const, label: 'Cron', icon: <Clock className="h-3.5 w-3.5" /> },
                { id: 'event' as const, label: 'Event', icon: <Zap className="h-3.5 w-3.5" /> },
                { id: 'dependency' as const, label: 'Dependency', icon: <GitBranch className="h-3.5 w-3.5" /> },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTriggerMode(t.id)}
                  aria-pressed={triggerMode === t.id}
                  className={`
                    flex items-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-xl text-xs font-medium border transition-all min-h-[44px] sm:min-h-0
                    ${triggerMode === t.id
                      ? 'bg-[#FFB52B]/12 text-[#B57700] dark:text-[#FFCF74] border-[#FFB52B]/30 shadow-sm shadow-[#FFB52B]/10'
                      : 'bg-white/80 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border-gray-200/80 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20'
                    }
                  `}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cron schedule picker */}
          {triggerMode === 'cron' && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Schedule Frequency *
              </label>

              {/* Preset radio buttons */}
              <div className="space-y-1.5" role="radiogroup" aria-label="Schedule frequency">
                {PRESETS.map((preset) => (
                  <label
                    key={preset.id}
                    className={`
                      flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-xl border cursor-pointer transition-all min-h-[44px]
                      ${selectedPreset === preset.id
                        ? 'bg-[#FFB52B]/12 border-[#FFB52B]/30 shadow-sm shadow-[#FFB52B]/10'
                        : 'bg-white/80 dark:bg-white/[0.04] border-gray-200/80 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="schedule-preset"
                      value={preset.id}
                      checked={selectedPreset === preset.id}
                      onChange={() => setSelectedPreset(preset.id)}
                      className="sr-only"
                    />
                    <div className={`
                      w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      ${selectedPreset === preset.id
                        ? 'border-[#FFB52B]'
                        : 'border-gray-400 dark:border-gray-500'
                      }
                    `}>
                      {selectedPreset === preset.id && (
                        <div className="w-2 h-2 rounded-full bg-[#FFB52B]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${selectedPreset === preset.id ? 'text-[#FFB52B]' : 'text-gray-700 dark:text-gray-300'}`}>
                        {preset.label}
                      </span>

                      {/* Inline pickers for daily */}
                      {preset.id === 'daily' && selectedPreset === 'daily' && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                          <select
                            value={dailyTime}
                            onChange={(e) => updateDailyTime(e.target.value)}
                            className={`min-w-[10.5rem] px-3 py-2 pr-10 text-sm sm:text-xs ${MODAL_CONTROL_BASE_CLASS} ${MODAL_SELECT_ARROW_CLASS}`}
                          >
                            {TIME_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </div>
                      )}

                      {/* Inline pickers for weekly */}
                      {preset.id === 'weekly' && selectedPreset === 'weekly' && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">on</span>
                          <select
                            value={weeklyDay}
                            onChange={(e) => setWeeklyDay(e.target.value)}
                            className={`min-w-[9rem] px-3 py-2 pr-10 text-sm sm:text-xs ${MODAL_CONTROL_BASE_CLASS} ${MODAL_SELECT_ARROW_CLASS}`}
                          >
                            {DAY_NAMES.map((d, i) => <option key={i} value={String(i)}>{d}</option>)}
                          </select>
                          <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                          <select
                            value={weeklyTime}
                            onChange={(e) => updateWeeklyTime(e.target.value)}
                            className={`min-w-[10.5rem] px-3 py-2 pr-10 text-sm sm:text-xs ${MODAL_CONTROL_BASE_CLASS} ${MODAL_SELECT_ARROW_CLASS}`}
                          >
                            {TIME_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </div>
                      )}

                      {/* Custom cron input */}
                      {preset.id === 'custom' && selectedPreset === 'custom' && (
                        <div className="mt-2">
                          <input
                            type="text"
                            value={customCron}
                            onChange={(e) => setCustomCron(e.target.value)}
                            placeholder="0 3 * * *"
                            aria-label="Custom cron expression"
                            className={`w-full px-3 py-1.5 text-xs font-mono ${MODAL_CONTROL_BASE_CLASS}`}
                          />
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                            Format: minute hour day-of-month month day-of-week
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Cron validation preview */}
              <div className={`
                flex items-start gap-2 px-3 py-2.5 rounded-xl border text-sm
                ${cronValidation.valid
                  ? 'bg-emerald-50 dark:bg-emerald-500/5 border-emerald-200 dark:border-emerald-500/20'
                  : 'bg-red-50 dark:bg-red-500/5 border-red-200 dark:border-red-500/20'
                }
              `}>
                {cronValidation.valid ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  {cronValidation.valid ? (
                    <>
                      <p className="text-emerald-600 dark:text-emerald-400 font-medium">{cronValidation.human}</p>
                      <p className="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-0.5">{effectiveCron}</p>
                      {nextRuns.length > 0 && (
                        <div className="mt-2 space-y-0.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Next runs</p>
                          {nextRuns.map((d, i) => (
                            <div key={i} className="flex items-center gap-1 text-[11px] text-gray-400">
                              <ChevronRight className="h-3 w-3 text-gray-600" />
                              {formatRunTime(d)}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-red-600 dark:text-red-400">{cronValidation.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Event triggers */}
          {triggerMode === 'event' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Event Triggers *
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {(availableEvents.length > 0
                    ? availableEvents.map((e) => e.name)
                    : [
                        'session.created', 'session.published', 'transcript.refined',
                        'metadata.finalized', 'upload.completed', 'youtube.synced',
                        'share.created', 'arrangement.version_created',
                        'video.source_downloaded', 'video.proxy_ready',
                        'video.clip_extracted', 'agent.session_completed',
                      ]
                  ).map((evt) => (
                    <button
                      key={evt}
                      type="button"
                      onClick={() => toggleEvent(evt)}
                      aria-pressed={selectedEvents.includes(evt)}
                      className={`
                        px-2.5 py-1.5 sm:px-2 sm:py-1 rounded-lg text-xs font-medium border transition-all min-h-[36px] sm:min-h-0
                        ${selectedEvents.includes(evt)
                          ? 'bg-amber-500/12 text-amber-700 dark:text-amber-300 border-amber-500/30'
                          : 'bg-white/80 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border-gray-200/80 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-700 dark:hover:text-gray-200'
                        }
                      `}
                    >
                      {evt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="event-delay" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Event Delay (seconds)
                </label>
                <input
                  id="event-delay"
                  type="number"
                  value={eventDelaySeconds}
                  onChange={(e) => setEventDelaySeconds(parseInt(e.target.value) || 0)}
                  min={0}
                  className={`w-24 px-3 py-2 text-sm ${MODAL_CONTROL_BASE_CLASS}`}
                />
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  Debounce period before the event-triggered run fires
                </p>
              </div>
            </>
          )}

          {/* Dependency */}
          {triggerMode === 'dependency' && (
            <div>
              <label htmlFor="depends-on-schedule" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Depends On Schedule *
              </label>
              <select
                id="depends-on-schedule"
                value={dependsOnScheduleId}
                onChange={(e) => setDependsOnScheduleId(e.target.value)}
                className={`w-full px-3 py-2 pr-10 text-sm ${MODAL_CONTROL_BASE_CLASS} ${MODAL_SELECT_ARROW_CLASS}`}
              >
                <option value="">Select a schedule...</option>
                {(existingSchedules || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                This schedule will fire when the selected schedule completes successfully
              </p>
            </div>
          )}

          {/* Prompt */}
          <div>
            <label htmlFor="schedule-prompt" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Prompt *</label>
            <textarea
              id="schedule-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={8}
              className={`w-full px-3 py-2 text-sm font-mono resize-y ${MODAL_CONTROL_BASE_CLASS}`}
              placeholder="Instructions for the scheduled agent..."
            />
          </div>

          {/* Advanced settings */}
          <details className="group">
            <summary className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer list-none flex items-center gap-1">
              <Plus className="h-3 w-3 group-open:rotate-45 transition-transform" />
              Advanced Settings
            </summary>
            <div className="mt-3 space-y-3 pl-0 sm:pl-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="schedule-model" className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">Model</label>
                  <input
                    id="schedule-model"
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="Default (Opus)"
                    className={`w-full px-3 py-1.5 text-xs ${MODAL_CONTROL_BASE_CLASS}`}
                  />
                </div>
                <div>
                  <label htmlFor="schedule-reasoning" className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">Reasoning Effort</label>
                  <select
                    id="schedule-reasoning"
                    value={reasoningEffort}
                    onChange={(e) => setReasoningEffort(e.target.value)}
                    className={`w-full px-3 py-1.5 pr-10 text-xs ${MODAL_CONTROL_BASE_CLASS} ${MODAL_SELECT_ARROW_CLASS}`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="schedule-max-turns" className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">Max Turns</label>
                  <input
                    id="schedule-max-turns"
                    type="number"
                    value={maxTurns}
                    onChange={(e) => setMaxTurns(parseInt(e.target.value) || 50)}
                    min={1}
                    className={`w-full px-3 py-1.5 text-xs ${MODAL_CONTROL_BASE_CLASS}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label htmlFor="schedule-max-runtime" className="text-[11px] text-gray-500 dark:text-gray-400">Max Runtime</label>
                      <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{formatDuration(maxRuntimeSeconds)}</span>
                    </div>
                    <input
                      id="schedule-max-runtime"
                      type="range"
                      min={60}
                      max={7200}
                      step={60}
                      value={maxRuntimeSeconds}
                      onChange={(e) => setMaxRuntimeSeconds(parseInt(e.target.value) || 600)}
                      className="w-full accent-[#FFB52B]"
                    />
                    <div className="flex justify-between text-[9px] text-gray-400">
                      <span>1m</span><span>15m</span><span>30m</span><span>1h</span><span>2h</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="schedule-cost-limit" className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">Cost Limit (USD, optional)</label>
                <input
                  id="schedule-cost-limit"
                  type="text"
                  value={maxCostUsd}
                  onChange={(e) => setMaxCostUsd(e.target.value)}
                  placeholder="No limit"
                  className={`w-32 px-3 py-1.5 text-xs ${MODAL_CONTROL_BASE_CLASS}`}
                />
              </div>
            </div>
          </details>

          {/* Submit */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-2 pb-2 sm:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 sm:py-2 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              aria-label="Create schedule"
              className="flex items-center justify-center gap-2 px-5 py-2.5 sm:py-2 rounded-xl text-sm font-medium
                bg-[#FFB52B] text-gray-900 hover:bg-[#FFB52B]/90
                transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateScheduleModal;
